import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CopyPlus, Trash2 } from "lucide-react";
import { parseSetComponents } from "@/components/cennik/KovanieSetDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  useCreateMaterial,
  useDeleteMaterial,
  useDodavatelia,
  useEdgeWidths,
  useKategorie,
  useStruktury,
  useUpdateMaterial,
  useMarzeKategorie,
  useNavrhMaterialu,
  useTypyDosiek,
  useVyrobcovia,
} from "@/api/hooks/useCennik";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { formatPct, normalizeNum, parseDecimal } from "@/lib/format";
import { useDebounce } from "@/lib/useDebounce";
import {
  HRUBKY_HRAN,
  PREDPONY_HRANIPEX,
  jeHttpOdkaz,
  rozoberOdkaz,
} from "@/lib/eshopOdkaz";
import { formatKategoria, kodyKategorii, materialMaOdpad } from "@/lib/kategorie";
import type { DruhMaterialu, Material, Sekcia } from "@/api/schemas";
import { StavZnaky } from "@/components/cennik/StavZnaky";

// `bal` (balenie) od KOLA 165 — Hranipex `pk` bez počtu kusov, plátok
// krytiek, TWD kartón bez počtu; bez neho sa taká položka nedala uložiť.
const ALLOWED_MJ = ["m2", "m", "ks", "set", "par", "bm", "kg", "l", "bal"] as const;
type MJ = (typeof ALLOWED_MJ)[number];
const MJ_LABEL: Partial<Record<MJ, string>> = { bal: "bal (balenie)" };

const NEW_DODAVATEL = "__new__";

const DRUH_LABELS: Record<DruhMaterialu, string> = {
  plosny: "Plošné materiály (DTD, MDF, sklo)",
  dlzkovy: "Hrany (hrana, hranol, pracovná doska)",
  kusovy: "Kovanie (kovanie, podperka, noha)",
};

// Akceptuj desatinnú čiarku aj bodku (SK zvyk „1,42") pred coerce na číslo.
const decimalNum = (validate: (n: z.ZodNumber) => z.ZodNumber) =>
  z.preprocess(
    (v) => (typeof v === "string" ? parseDecimal(v) : v),
    validate(z.coerce.number()),
  );

const schema = z.object({
  id_kod: z.string().max(100).optional().or(z.literal("")),
  nazov: z.string().min(1, "Názov je povinný").max(255),
  druh: z.enum(["plosny", "dlzkovy", "kusovy"]),
  mj: z.enum(ALLOWED_MJ),
  dekor_kod: z.string().max(50).optional().or(z.literal("")),
  nakup_cena: decimalNum((n) => n.min(0, "Nákupná cena musí byť ≥ 0")),
  rezia_pct: decimalNum((n) => n.min(0, "Min 0%").max(100, "Max 100%")),
  praca_eur_per_mj: decimalNum((n) => n.min(0)),
  nadmiera_pct: decimalNum((n) => n.min(0).max(100)),
  z_mm: z.string().optional().or(z.literal("")),
  sirka_mm: z.string().optional().or(z.literal("")),
  hrubka_mm: z.string().optional().or(z.literal("")),
  x_mm: z.string().optional().or(z.literal("")),
  y_mm: z.string().optional().or(z.literal("")),
  // Hodnota ide do `href` — `javascript:` by spravilo XSS na vlastnej
  // appke (tá istá pasca ako pri eshop šablónach v KOLE 72). Server to
  // kontroluje tiež, toto je len skorší a zrozumiteľnejší odpor.
  eshop_url: z
    .string()
    .max(500)
    .optional()
    .or(z.literal(""))
    .refine((v) => !v || jeHttpOdkaz(v), {
      message: "Odkaz musí začínať http:// alebo https://",
    }),
});

type FormData = z.infer<typeof schema>;

const NEW_KATEGORIA = "__new_kat__";
/** Radix Select nevie prázdnu hodnotu — „bez štruktúry" má vlastný kľúč. */
const BEZ_STRUKTURY = "__bez__";
/**
 * Prvý typ registra (`GET /cennik/typy-dosiek`) — doterajšie správanie:
 * dekorová doska, kategória z hrúbky. Platí aj kým sa register načíta.
 */
const PREDVOLENY_TYP_DOSKY = "lamino_dtd";

/** Kroky sprievodcu pridávaním. */
type KrokPridania = "co" | "odkial" | "udaje";

/**
 * Čo sa pridáva (alebo upravuje) — z toho plynie, ktoré kategórie okno
 * ponúkne (KOLO 169). Druh na to nestačí: dĺžkové sú hrany, pracovné
 * dosky aj krytky na metre a každé patrí do inej sekcie cenníka.
 */
type Trieda = "doska" | "pd" | "hrana" | "kovanie";

/** Sekcia cenníka, z ktorej okno berie kategórie. */
const SEKCIA_TRIEDY: Record<Trieda, Sekcia> = {
  doska: "materialy",
  pd: "materialy",
  hrana: "hrany",
  kovanie: "kovanie",
};

/** Pracovná doska alebo zástena — dĺžková, ale doska (hrúbka, formát). */
function jeKategoriaDosky(kategoria: string | null | undefined): boolean {
  const k = kategoria ?? "";
  return k.startsWith("PRACOVNA_DOSKA") || k.startsWith("ZASTENA");
}

/** Trieda podľa druhu — predvyplnenie z importu, záložka Cenníka. */
function triedaPreDruh(druh: DruhMaterialu, kategoria?: string | null): Trieda {
  if (druh === "kusovy") return "kovanie";
  if (druh === "plosny") return "doska";
  return jeKategoriaDosky(kategoria) ? "pd" : "hrana";
}

/** Trieda uloženej položky — sekciu vie server (`Material.sekcia`). */
function triedaMaterialu(m: Material): Trieda {
  if (m.druh === "kusovy" || m.sekcia === "kovanie") return "kovanie";
  if (m.druh === "dlzkovy" && m.sekcia === "materialy") return "pd";
  return triedaPreDruh(m.druh, m.kategoria);
}

/**
 * Čo sa dá pridať — voľba nastaví druh, mj aj kategóriu a rozhodne,
 * ktoré polia sa vôbec ukážu. Kovanie sa nebude pýtať na dekor a doska
 * na šírku hrany.
 */
const CO_PRIDAVAM: {
  kluc: Trieda;
  nazov: string;
  popis: string;
  druh: DruhMaterialu;
  mj: MJ;
  kategoria?: string;
}[] = [
  {
    kluc: "doska",
    nazov: "Plošná doska",
    popis: "DTD, MDF, škárovka, preglejka, sololit… — predáva sa na m²",
    druh: "plosny",
    mj: "m2",
  },
  {
    kluc: "hrana",
    nazov: "Hrana",
    popis: "ABS páska — predáva sa na bežné metre",
    druh: "dlzkovy",
    mj: "bm",
  },
  {
    kluc: "pd",
    nazov: "Pracovná doska",
    popis: "Kupuje sa hotová na bežné metre (tyč), nie ako tabuľa",
    druh: "dlzkovy",
    mj: "m",
    kategoria: "PRACOVNA_DOSKA",
  },
  {
    kluc: "kovanie",
    nazov: "Kovanie",
    popis: "Závesy, výsuvy, úchytky na kusy; profily a koľajnice na metre",
    druh: "kusovy",
    mj: "ks",
  },
];

export interface MaterialEditDialogProps {
  /** null = create mode, Material = edit mode. */
  material: Material | null;
  /** Predvolený druh podľa aktuálneho tabu cenníka. */
  defaultDruh?: DruhMaterialu;
  /**
   * Predvyplnenie pre create režim — napr. z položky importu, aby sa
   * nový dekor nemusel prepisovať ručne.
   */
  predvyplnene?: Predvyplnenie;
  /**
   * Vytvorený materiál. `useCreateMaterial` ho vracia, dialóg ho dovtedy
   * zahadzoval — import ho vďaka tomu vie rovno priradiť.
   */
  onCreated?: (material: Material) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Bez práva na sekciu položky (KOLO 231) — tovar mení obchodník, výrobu
   * rozpočtár. Okno ukáže údaje, uložiť ani vymazať sa nedá.
   */
  lenCitanie?: string | null;
}

/** Čo sa dá predvyplniť zvonku (create režim). */
export interface Predvyplnenie {
  nazov?: string;
  druh?: DruhMaterialu;
  mj?: string;
  dekor_kod?: string;
  kategoria?: string;
  z_mm?: string;
  sirka_mm?: string;
  hrubka_mm?: string;
}

/** Stĺpce, v ktorých môže byť kód materiálu (poradie ako `ID_POLIA`). */
type IdStlpec =
  | "hranipex_id"
  | "jafholz_id"
  | "twd_id"
  | "hafele_id"
  | "demos_id"
  | "sklo_mier_id"
  | "nabytkar_id"
  | "vlastny_id"
  | "daex_id";

/**
 * Do ktorého stĺpca ide kód.
 *
 * Kód sa zapisuje do stĺpca, v ktorom **už je** — úprava starej Daex
 * položky ju nesmie presunúť inam a tým zviditeľniť. Nová položka ide
 * podľa dodávateľa a neznámy či prázdny do `vlastny_id`.
 *
 * Fallback bol do KOLA 93 `daex_id`, lenže `daex_id IS NOT NULL` je
 * marker starej Daex databázy a skrýva riadok v cenníku, v pickeri
 * **aj** v návrhoch importu — taká položka zmizla úplne.
 */
function idColumnForDodavatel(dod: string, existujuci?: Material | null): IdStlpec {
  if (existujuci) {
    const drzitel = ID_STLPCE.find((c) => existujuci[c]);
    if (drzitel) return drzitel;
  }
  const d = dod.toLowerCase();
  if (d.includes("hranipex")) return "hranipex_id";
  if (d.includes("jaf")) return "jafholz_id";
  if (d.includes("twd")) return "twd_id";
  if (d.includes("hafele") || d.includes("häfele")) return "hafele_id";
  if (d.includes("demos")) return "demos_id";
  if (d.includes("sklo") || d.includes("mier")) return "sklo_mier_id";
  // Import Nábytkára aj odkaz do eshopu čítajú `nabytkar_id` (KOLO 169)
  if (d.includes("nabytkar") || d.includes("nábytkár")) return "nabytkar_id";
  return "vlastny_id";
}

/**
 * Stĺpec pre kód, ktorý skladá appka (KOLO 169). Je to náš kód, nie číslo
 * artikla dodávateľa, preto `vlastny_id`. JafHolz si kódy skladá v importéri
 * sám (`jafholz_id`) a hrana Hranipexu nesie propagačnú skupinu
 * v `hranipex_id` ako z importu — inde by ich import nespároval.
 */
function stlpecSkladanehoKodu(dod: string, hranipexHrana: boolean): IdStlpec {
  if (hranipexHrana) return "hranipex_id";
  return dod.toLowerCase().includes("jaf") ? "jafholz_id" : "vlastny_id";
}

const ID_STLPCE: readonly IdStlpec[] = [
  "hranipex_id",
  "jafholz_id",
  "twd_id",
  "hafele_id",
  "demos_id",
  "sklo_mier_id",
  "nabytkar_id",
  "vlastny_id",
  "daex_id",
];

/** Pekný label pre ID input podľa stĺpca kódu (bez zbytočného "(Daex)"). */
const ID_LABEL: Record<IdStlpec, string> = {
  hranipex_id: "ID Hranipex",
  jafholz_id: "ID JafHolz",
  twd_id: "ID TWD",
  hafele_id: "ID Häfele",
  demos_id: "ID Demos",
  sklo_mier_id: "ID Sklo Mier",
  nabytkar_id: "ID Nábytkár",
  vlastny_id: "ID",
  daex_id: "ID Daex",
};

/** Hrúbka z textu — „0,8", „0,8 mm" aj „18" → „0.8" / „18" (KOLO 233). */
export function hrubkaZTextu(v: string | null | undefined): string {
  return parseDecimal(v).replace(/\s*mm$/i, "").trim();
}

/** `"22.00"` → `"22"`, `"0,45"` → `"0.45"` — hodnota výberu rozmeru hrany. */
function hodnotaMm(v: string | null | undefined): string {
  const n = normalizeNum(parseDecimal(v));
  return Number(n) > 0 ? n : "";
}

/** Primárny ID z materiálu (podľa toho ktorý stĺpec je vyplnený). */
function primaryIdOf(m: Material | null): string {
  if (!m) return "";
  for (const stlpec of ID_STLPCE) {
    const hodnota = m[stlpec];
    if (hodnota) return hodnota;
  }
  return "";
}

export function MaterialEditDialog({
  material: materialProp,
  defaultDruh,
  predvyplnene,
  onCreated,
  open,
  onOpenChange,
  lenCitanie = null,
}: MaterialEditDialogProps): JSX.Element {
  /**
   * Kópia položky (KOLO 226 — user: „v cenníku mi dovoľ z tohto materiálu
   * vytvoriť ďalší, nech kolega použije parametre a len trochu pomení").
   * Okno sa prepne na **novú** položku predvyplnenú z originálu — nie
   * úpravou `material`, lebo uloženie by vzalo jeho kódy dodávateľa.
   */
  const [kopia, setKopia] = useState<Material | null>(null);
  useEffect(() => {
    if (!open) setKopia(null);
  }, [open, materialProp]);
  const material = kopia ? null : materialProp;
  /** Odkiaľ sa predvypĺňa — upravovaná položka alebo originál kópie. */
  const vzor = kopia ?? materialProp;
  const isEdit = !!material;
  /**
   * Uložená položka s kódom ho nemení — je to identita pre import cenníka
   * a meno dosky v Cut Rite (KOLO 169).
   */
  const maUlozenyKod = !!primaryIdOf(material);
  // Set kovaní (Spojiť kovania) — komponenty zo supplier_data
  // Kusy v sete (KOLO 228), starší set s duplicitou zložený do počtu
  const setKomponenty = parseSetComponents(material);
  const createMutation = useCreateMaterial();
  const updateMutation = useUpdateMaterial();
  const deleteMutation = useDeleteMaterial();
  const [druh, setDruh] = useState<DruhMaterialu>("plosny");
  const [trieda, setTrieda] = useState<Trieda>("doska");
  const [mj, setMj] = useState<MJ>("m2");
  const [dodavatel, setDodavatel] = useState<string>("");
  const [novyDodavatel, setNovyDodavatel] = useState("");
  const [vyrobca, setVyrobca] = useState<string>("");
  const [struktura, setStruktura] = useState<string>("");
  // Drevná štruktúra (smer vlákien) — mapuje sa na smer_vlakien_default X/0
  const [maStrukturu, setMaStrukturu] = useState(false);
  /**
   * Typ plošnej dosky (KOLO 167) — škárovka sa skladá z dreviny, kvality
   * a formátu, nie z dekoru. Smer vlákien predvolí typ, kým naň user
   * nesiahne (`smerRucne`).
   */
  const [typDosky, setTypDosky] = useState(PREDVOLENY_TYP_DOSKY);
  const [zaklad, setZaklad] = useState("");
  const [povrch, setPovrch] = useState("");
  const [smerRucne, setSmerRucne] = useState(false);
  /**
   * Hrana Hranipexu (KOLO 169) — kód je propagačná skupina ako v importe
   * (`HD 211381`, lepená `HD 211381 lep.`).
   */
  const [predpona, setPredpona] = useState("");
  const [cisloSkupiny, setCisloSkupiny] = useState("");
  const [lepidlo, setLepidlo] = useState(false);
  /** Názov napísal user — návrh zo skupiny ho už neprepíše. */
  const [nazovRucne, setNazovRucne] = useState(false);
  const [kategoria, setKategoria] = useState<string>("");
  const [novaKategoria, setNovaKategoria] = useState("");
  /**
   * Sprievodca — **len pre nový materiál**. Edit ide rovno na formulár,
   * tam už človek vie, čo mení. User: „ten čo manuálne pridáva materiál
   * nevie, v akom štýle má písať ID."
   */
  const [krok, setKrok] = useState<KrokPridania>("co");
  /**
   * Odvodené parametre sa dopĺňajú samé, kým do nich user nesiahne —
   * potom sú jeho. Bez týchto troch príznakov by mu návrh prepisoval
   * ručne zadané číslo pri každom ďalšom dopyte.
   */
  const [marzeRucne, setMarzeRucne] = useState(false);
  const [kategoriaRucne, setKategoriaRucne] = useState(false);
  const [formatRucne, setFormatRucne] = useState(false);
  const [riadiSaKategoriou, setRiadiSaKategoriou] = useState(false);
  /** „Riadiť sa pravidlom" (KOLO 168) — uloženie zhodí ručný smer. */
  const [riadiSaPravidlom, setRiadiSaPravidlom] = useState(false);

  // Okno je na stránke Cenníka stále pripojené — zatvorené nič nenačítava
  // (KOLO 225: štyri dotazy pri každom otvorení Cenníka navyše)
  const dodavateliaQuery = useDodavatelia(undefined, { enabled: open });
  /**
   * Kategórie podľa toho, **čo sa pridáva** (KOLO 169) — nie podľa
   * záložky, z ktorej sa okno otvorilo. Kovanie pridané zo záložky
   * Materiály ponúkalo „DTD 18 mm" a kategória kovania sa nedala vybrať.
   * Uložená položka má sekciu zo servera, kým sa jej nezmení druh.
   */
  const sekciaKategorii: Sekcia =
    material?.sekcia && druh === material.druh ? material.sekcia : SEKCIA_TRIEDY[trieda];
  const kategorieQuery = useKategorie(
    // Kovanie je kusové aj na metre (krytky, profily) — bez druhu
    { sekcia: sekciaKategorii, druh: sekciaKategorii === "kovanie" ? undefined : druh },
    { enabled: open },
  );
  const kategorieOptions = useMemo(
    () => kodyKategorii(kategorieQuery.data?.data.polozky ?? []),
    [kategorieQuery.data],
  );
  const vyrobcoviaQuery = useVyrobcovia(undefined, { enabled: open });
  const strukturyQuery = useStruktury({ enabled: open });
  const struktury = strukturyQuery.data?.data ?? [];
  // Druh dosky ponúka len sprievodca novej položky
  const typyDosiekQuery = useTypyDosiek(open && !isEdit);
  const typyDosiek = typyDosiekQuery.data?.data ?? [];
  const vybranyTyp =
    druh === "plosny" ? (typyDosiek.find((t) => t.kluc === typDosky) ?? null) : null;
  /** Kód začína dekorom — aj kým sa register nenačítal (predvolený typ). */
  const dekorovyTyp = vybranyTyp ? vybranyTyp.dekorova : true;
  const formatVKode = vybranyTyp?.format_v_kode ?? false;
  const dodavatelText = dodavatel === NEW_DODAVATEL ? novyDodavatel : dodavatel;
  /**
   * Kód dosky (aj pracovnej) a hrany **len skladá appka** — rozhodnutie usera
   * (KOLO 169): je to identita pre import aj meno dosky v Cut Rite, ručný
   * prepis by schému obišiel. Kovanie sa volá číslom dodávateľa a opisuje sa.
   */
  const kodSkladany = trieda !== "kovanie";
  /** Kód sa skladá teraz: nová položka, alebo uložená, ktorá kód nemá. */
  const skladaSa = kodSkladany && !maUlozenyKod;
  /** Hrana Hranipexu sa volá propagačnou skupinou ako v importe. */
  const hranipexHrana =
    trieda === "hrana" && dodavatelText.trim().toLowerCase() === "hranipex";
  /** Šírky hrán z cenníka — šírka hrany sa vyberá, nepíše (KOLO 169). */
  const sirkyHranQuery = useEdgeWidths({ enabled: open && trieda === "hrana" });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: defaultsFromMaterial(material, predvyplnene),
  });

  function mjForDruh(d: DruhMaterialu): MJ {
    return d === "plosny" ? "m2" : d === "dlzkovy" ? "m" : "ks";
  }

  function handleDruhChange(d: DruhMaterialu): void {
    if (!d) return; // ozvena Radixu, ako pri kategórii
    // Pracovná doska ostáva, čím je. Kovanie na metre sa zapína MJ
    // (`zmenMj`) — výber „Hrany" z kovania znamená naozaj hranu
    const nova = d === "dlzkovy" && trieda === "pd" ? trieda : triedaPreDruh(d);
    // Iný spôsob kódu — skladaný kód nesmie ostať ako opísaný a naopak
    if (nova !== trieda && !maUlozenyKod) setValue("id_kod", "");
    setDruh(d);
    setMj(mjForDruh(d));
    setTrieda(nova);
  }

  /**
   * Kovanie na metre je dĺžkový materiál (KOLO 176) — kráti sa z tyče,
   * takže ide do nárezového plánu. MJ ``m`` preto prepne druh sám; iná
   * MJ vráti kusové. Ostatné triedy MJ druh nemenia.
   */
  function zmenMj(v: MJ): void {
    if (!v) return; // ozvena Radixu
    setMj(v);
    if (trieda !== "kovanie") return;
    const novyDruh: DruhMaterialu = v === "m" || v === "bm" ? "dlzkovy" : "kusovy";
    if (novyDruh === "dlzkovy" && v === "bm") setMj("m");
    // Kus tyč nemá — skrytá dĺžka by sa inak uložila
    if (novyDruh === "kusovy") setValue("x_mm", "");
    setDruh(novyDruh);
    setValue("druh", novyDruh);
  }

  useEffect(() => {
    if (open) {
      const defaults = defaultsFromMaterial(vzor, predvyplnene);
      // Kópia nesie parametre, nie identitu: kód (z neho sa zloží nový)
      // a odkaz na produkt originálu do novej položky nepatria
      reset(kopia ? { ...defaults, id_kod: "", eshop_url: "" } : defaults);
      // Create mode: predvyplň druh z položky importu, inak z tabu
      const initDruh = (vzor?.druh ??
        predvyplnene?.druh ??
        defaultDruh ??
        "plosny") as DruhMaterialu;
      setDruh(initDruh);
      setTrieda(
        vzor
          ? triedaMaterialu(vzor)
          : triedaPreDruh(initDruh, predvyplnene?.kategoria),
      );
      setMj(
        (vzor?.mj as MJ | undefined) ??
          (predvyplnene?.mj as MJ | undefined) ??
          mjForDruh(initDruh),
      );
      setDodavatel(vzor?.dodavatel ?? "");
      setNovyDodavatel("");
      setVyrobca(vzor?.vyrobca ?? "");
      setStruktura(vzor?.struktura_povrchu ?? "");
      setMaStrukturu(
        (vzor?.smer_vlakien_default ?? "0") !== "0",
      );
      setTypDosky(PREDVOLENY_TYP_DOSKY);
      setZaklad("");
      setPovrch("");
      setSmerRucne(!!vzor);
      setPredpona("");
      setCisloSkupiny("");
      setLepidlo(false);
      setNazovRucne(!!vzor);
      setKategoria(vzor?.kategoria ?? predvyplnene?.kategoria ?? "");
      setNovaKategoria("");
      // Predvyplnené (z importu) už druh vie — sprievodca prvý krok
      // preskočí, inak by sa pýtal na to, čo je jasné; kópia ide rovno
      // na údaje
      setKrok(kopia ? "udaje" : predvyplnene?.druh ? "odkial" : "co");
      setMarzeRucne(false);
      setKategoriaRucne(!!vzor || !!predvyplnene?.kategoria);
      setFormatRucne(!!vzor);
      setRiadiSaKategoriou(false);
      setRiadiSaPravidlom(false);
    }
    // `predvyplnene` je stabilné v rámci jedného otvorenia dialógu
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, material, kopia, defaultDruh, reset]);

  // Druh dosky kópie podľa kategórie originálu (škárovka, preglejka…) —
  // zoznam typov príde až po otvorení, preto zvlášť (KOLO 226)
  useEffect(() => {
    if (!kopia || kopia.druh !== "plosny") return;
    const typ = typyDosiek.find((t) => t.kategoria && t.kategoria === kopia.kategoria);
    if (!typ) return;
    setTypDosky(typ.kluc);
    // Drevina a kvalita z kódu originálu — bez nich sa kód škárovky či
    // biodosky nezloží a user by ich musel hľadať
    const casti = rozlozKodDosky(primaryIdOf(kopia));
    if (casti && typ.pole_zaklad) setZaklad(casti.zaklad);
    if (casti?.povrch && typ.pole_povrch) setPovrch(casti.povrch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kopia, typyDosiekQuery.data]);

  /**
   * Kód skladá **backend** — importér, migrácia aj sprievodca musia dať
   * ten istý reťazec (lekcia z KOLA 77). Duplicita chodí v tej istej
   * odpovedi, takže sa o nej user dozvie pred uložením.
   */
  const sledovanyDekor = watch("dekor_kod");
  const sledovanaSirka = watch("sirka_mm");
  const sledovanaHrubka = watch("hrubka_mm");
  const sledovanaHrubkaDosky = watch("z_mm");
  const sledovanyFormatX = watch("x_mm");
  const sledovanyFormatY = watch("y_mm");
  // Názov a výrobca idú do návrhu smeru vlákien (drevina v mene, štruktúra
  // výrobcu) — až keď sa dopíšu, nie dotaz na každé písmeno
  const sledovanyNazov = useDebounce(watch("nazov") ?? "", 300);
  const sledovanyVyrobca = useDebounce(vyrobca, 300);
  /** Dekorová doska — smer vlákien navrhne server (KOLO 168). */
  const smerZNavrhu = druh === "plosny" && dekorovyTyp;
  // Drevná kresba má vlastnú nadmieru (30 % namiesto 20 %) — bez smeru by
  // nová doska dostala uni číslo a `marze_rucne` by ju zamkol pred importom
  const smerPreMarze =
    druh === "plosny" ? (maStrukturu ? "X" : "0") : undefined;
  /**
   * Šírka a hrúbka hrany len z ponuky (KOLO 169) — mimo nej sa kód
   * nezloží (do návrhu nejde), takže sa položka ani neuloží.
   */
  const sirkyHran = (sirkyHranQuery.data?.data ?? []).map((w) => String(w.sirka_mm));
  const sirkaHrany = hodnotaMm(sledovanaSirka);
  const hrubkaHrany = hodnotaMm(sledovanaHrubka);
  const sirkaPovolena = !skladaSa || sirkyHran.includes(sirkaHrany);
  const hrubkaPovolena =
    !skladaSa || (HRUBKY_HRAN as readonly string[]).includes(hrubkaHrany);
  const navrhQuery = useNavrhMaterialu(
    !open || (isEdit && !skladaSa)
      ? null
      : {
          druh,
          sekcia: SEKCIA_TRIEDY[trieda],
          dodavatel: dodavatelText,
          // Dekor a štruktúra patria len dekorovým doskám — škárovka sa
          // skladá z dreviny a kvality (KOLO 167)
          dekor: dekorovyTyp ? sledovanyDekor || undefined : undefined,
          // „0,8" aj „0,8 mm" (KOLO 233) — server berie len číslo s bodkou,
          // inak 422 a kód s kategóriou sa nevyplnia
          z_mm: hrubkaZTextu(sledovanaHrubkaDosky) || undefined,
          struktura: dekorovyTyp ? struktura || undefined : undefined,
          sirka_mm: trieda !== "hrana" || sirkaPovolena ? sledovanaSirka || undefined : undefined,
          hrubka_mm:
            trieda !== "hrana" || hrubkaPovolena ? sledovanaHrubka || undefined : undefined,
          kategoria_hint: kategoria === NEW_KATEGORIA ? undefined : kategoria,
          smer_vlakien: smerPreMarze,
          typ_dosky: druh === "plosny" ? typDosky : undefined,
          zaklad: dekorovyTyp ? undefined : zaklad || undefined,
          povrch: dekorovyTyp ? undefined : povrch || undefined,
          // Formát je v kóde pri drevených doskách a šírka pri pracovnej doske
          x_mm: formatVKode || trieda === "pd" ? sledovanyFormatX || undefined : undefined,
          y_mm: formatVKode || trieda === "pd" ? sledovanyFormatY || undefined : undefined,
          // Smer vlákien podľa štruktúry výrobcu a dreviny v názve (KOLO 168)
          vyrobca: smerZNavrhu ? sledovanyVyrobca || undefined : undefined,
          nazov: smerZNavrhu ? sledovanyNazov || undefined : undefined,
          // Hrana Hranipexu — propagačná skupina (KOLO 169)
          predpona: hranipexHrana ? predpona || undefined : undefined,
          cislo: hranipexHrana ? cisloSkupiny || undefined : undefined,
          lepidlo: hranipexHrana && lepidlo ? "true" : undefined,
        },
  );
  const navrhKodu = navrhQuery.data?.data ?? null;

  /**
   * Smer vlákien dekorovej dosky navrhne server tým istým pravidlom ako
   * import cenníka (KOLO 168) — štruktúra výrobcu, drevina v názve, dekor
   * Egger `H…`. Kým user na začiarknutie nesiahne (`smerRucne`), riadi ho
   * návrh; drevené typy majú smer pevne z registra (`zmenTypDosky`).
   */
  const smerNavrh = navrhKodu?.smer_navrh ?? null;
  useEffect(() => {
    if (isEdit || smerRucne || smerNavrh === null) return;
    if (vybranyTyp && vybranyTyp.smer_vlakien !== "podla_struktury") return;
    setMaStrukturu(smerNavrh === "X");
  }, [smerNavrh, isEdit, smerRucne, vybranyTyp]);

  /**
   * Po ručnej oprave kategórie v edit režime **ponúkni** jej marže
   * (KOLO 98).
   *
   * Odvodzovací efekt nižšie je v edit režime vypnutý zámerne — user si
   * mohol prácu či réžiu nastaviť naschvál a tichý prepis by mu ju vzal.
   * Lenže bez prepočtu oprava kategórie cenu nezmení, a to je celý jej
   * zmysel. Preto sa čísla ukážu a doplní ich až kliknutie — rovnaký vzor
   * ako „Predvyplniť" (KOLO 76) a „Použiť do formulára" (KOLO 62b).
   */
  const [povodnaKategoria] = useState(material?.kategoria ?? null);
  const kategoriaZmenena =
    isEdit &&
    !!kategoria &&
    kategoria !== NEW_KATEGORIA &&
    kategoria !== povodnaKategoria;
  const marzeQuery = useMarzeKategorie(
    druh,
    kategoriaZmenena ? kategoria : null,
    smerPreMarze,
  );
  const marzeNovej = marzeQuery.data?.data ?? null;

  function pouziMarzeKategorie(): void {
    if (!marzeNovej) return;
    setValue("rezia_pct", Number(marzeNovej.rezia_pct));
    setValue("praca_eur_per_mj", Number(marzeNovej.praca_eur_per_mj));
    setValue("nadmiera_pct", Number(marzeNovej.nadmiera_pct));
    setMarzeRucne(true);
  }

  /**
   * Kategória, marže a formát sa **odvodia**, nie vypytujú.
   *
   * Marže sú v `pricing_rules.yaml` per kategória a kategória plošnej
   * dosky je daná hrúbkou — tie isté čísla, aké dostane materiál pri
   * importe cenníka. Formát je najčastejší formát tej kategórie
   * v cenníku (DTD 2800×2070). User: *„práca, nadmiera, réžia súvisia
   * ako globálne meníš"*.
   */
  useEffect(() => {
    if (!navrhKodu) return;
    // Skladaný kód vlastní server (KOLO 169) — aj prázdny: kód iných polí
    // (K682_22x1 pred prepnutím na Hranipex) sa nesmie uložiť
    if (skladaSa) setValue("id_kod", navrhKodu.kod);
    if (isEdit) return;
    if (navrhKodu.kategoria && !kategoriaRucne) setKategoria(navrhKodu.kategoria);
    if (!marzeRucne) {
      if (navrhKodu.rezia_pct != null)
        setValue("rezia_pct", Number(navrhKodu.rezia_pct));
      if (navrhKodu.praca_eur_per_mj != null)
        setValue("praca_eur_per_mj", Number(navrhKodu.praca_eur_per_mj));
      if (navrhKodu.nadmiera_pct != null)
        setValue("nadmiera_pct", Number(navrhKodu.nadmiera_pct));
    }
    if (!formatRucne && navrhKodu.x_mm && navrhKodu.y_mm) {
      setValue("x_mm", String(navrhKodu.x_mm));
      setValue("y_mm", String(navrhKodu.y_mm));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navrhKodu, isEdit, skladaSa, marzeRucne, kategoriaRucne, formatRucne]);

  /**
   * Skupina, ktorú cenník už pozná, doplní prázdne polia novej hrany
   * Hranipexu (KOLO 169) — predponu (odkaz s číslom artikla ju nenesie),
   * dekor, štruktúru, výrobcu a názov v tvare cenníka. Čo user napísal,
   * ostáva jeho.
   */
  const skupina = navrhKodu?.skupina ?? null;
  useEffect(() => {
    if (!skupina || !hranipexHrana || !skladaSa) return;
    if (!predpona) setPredpona(skupina.predpona);
    if (!sledovanyDekor && skupina.dekor) setValue("dekor_kod", skupina.dekor);
    if (!struktura && skupina.struktura) setStruktura(skupina.struktura);
    if (!vyrobca && skupina.vyrobca) setVyrobca(skupina.vyrobca);
    if (!nazovRucne && skupina.nazov) setValue("nazov", skupina.nazov);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skupina]);

  /**
   * Iný typ dosky = iná kategória a iný kód; smer vlákien predvolí typ
   * (drevo áno), kým ho user sám nezmenil.
   */
  function zmenTypDosky(kluc: string): void {
    setTypDosky(kluc);
    setKategoriaRucne(false);
    const typ = typyDosiek.find((t) => t.kluc === kluc);
    if (typ && !smerRucne && typ.smer_vlakien !== "podla_struktury") {
      setMaStrukturu(typ.smer_vlakien === "X");
    }
  }

  /**
   * Z adresy sa dá vytiahnuť viac než kód — dekor, štruktúra, výrobca, typ
   * dosky, hrúbka a formát; pri Hranipexe skupina, lepidlo a rozmer hrany.
   */
  function prevezmiOdkaz(adresa: string): void {
    const r = rozoberOdkaz(adresa, {
      struktury: struktury.map((x) => x.kod),
      vyrobcovia: vyrobcoviaQuery.data?.data ?? [],
      typyDosiek,
    });
    if (r.typDosky && druh === "plosny") zmenTypDosky(r.typDosky);
    if (r.zaklad) setZaklad(r.zaklad);
    if (r.povrch) setPovrch(r.povrch);
    if (jeHttpOdkaz(adresa)) setValue("eshop_url", adresa);
    if (r.dodavatel) setDodavatel(r.dodavatel);
    if (r.vyrobca) setVyrobca(r.vyrobca);
    if (r.struktura) setStruktura(r.struktura);
    if (r.dekor) setValue("dekor_kod", r.dekor);
    // Kód z adresy patrí len kovaniu — dosky a hrany ho skladá appka z polí
    // (KOLO 169); do ID by sa dostalo, čo eshop práve hľadal
    if (r.kod && !kodSkladany) setValue("id_kod", r.kod);
    if (r.nazov) setValue("nazov", r.nazov);
    if (trieda === "doska" || trieda === "pd") {
      if (r.hrubkaDosky) setValue("z_mm", r.hrubkaDosky);
      if (r.formatDlzka && r.formatSirka) {
        setValue("x_mm", r.formatDlzka);
        setValue("y_mm", r.formatSirka);
        setFormatRucne(true);
      }
    }
    if (trieda === "hrana") {
      if (r.predpona) setPredpona(r.predpona);
      if (r.cisloSkupiny) setCisloSkupiny(r.cisloSkupiny);
      if (r.lepidlo !== null) setLepidlo(r.lepidlo);
      if (r.sirkaHrany) setValue("sirka_mm", r.sirkaHrany);
      if (r.hrubkaHrany) setValue("hrubka_mm", r.hrubkaHrany);
    }
  }

  async function onSubmit(values: FormData): Promise<void> {
    const finalDodavatel =
      dodavatel === NEW_DODAVATEL ? novyDodavatel.trim() : dodavatel;
    // Uložený kód ostáva vo svojom stĺpci; nový skladaný je náš (KOLO 169)
    const idCol =
      kodSkladany && !maUlozenyKod
        ? stlpecSkladanehoKodu(finalDodavatel, hranipexHrana)
        : idColumnForDodavatel(finalDodavatel, material);
    const idKod = values.id_kod?.trim() || null;

    const payload = {
      // `daex_id` sa z UI **nezapisuje** — je to marker starej Daex
      // databázy a taký riadok cenník aj návrhy skrývajú. Pri úprave
      // Daex položky sa zachová to, čo v nej už je.
      daex_id: idCol === "daex_id" ? idKod : (material?.daex_id ?? null),
      vlastny_id: idCol === "vlastny_id" ? idKod : (material?.vlastny_id ?? null),
      hranipex_id: idCol === "hranipex_id" ? idKod : (material?.hranipex_id ?? null),
      jafholz_id: idCol === "jafholz_id" ? idKod : (material?.jafholz_id ?? null),
      twd_id: idCol === "twd_id" ? idKod : (material?.twd_id ?? null),
      hafele_id: idCol === "hafele_id" ? idKod : (material?.hafele_id ?? null),
      demos_id: idCol === "demos_id" ? idKod : (material?.demos_id ?? null),
      sklo_mier_id:
        idCol === "sklo_mier_id" ? idKod : (material?.sklo_mier_id ?? null),
      nabytkar_id: idCol === "nabytkar_id" ? idKod : (material?.nabytkar_id ?? null),
      nazov: values.nazov,
      druh,
      mj,
      kategoria:
        (kategoria === NEW_KATEGORIA ? novaKategoria.trim() : kategoria) || null,
      // Skladaný kód má dekor veľkými písmenami — v položke tak isto, inak
      // by ho párovanie hrán (dekor dosky = dekor hrany) nenašlo
      dekor_kod:
        druh === "kusovy" || !dekorovyTyp
          ? null
          : (skladaSa ? values.dekor_kod?.trim().toUpperCase() : values.dekor_kod) || null,
      nakup_cena: String(values.nakup_cena),
      rezia_pct: String(values.rezia_pct),
      praca_eur_per_mj: String(values.praca_eur_per_mj),
      // Odpad % má zmysel len pri doskách — pri hranách/kovaní ho neukladáme
      nadmiera_pct: maOdpad ? String(values.nadmiera_pct) : "0",
      z_mm: hrubkaZTextu(values.z_mm) || null,
      sirka_mm: parseDecimal(values.sirka_mm) || null,
      hrubka_mm: parseDecimal(values.hrubka_mm) || null,
      // Priamy odkaz na produkt — šablóny z Nastavení vedia zložiť len
      // vyhľadanie podľa kódu (KOLO 76–78)
      eshop_url: values.eshop_url?.trim() || null,
      // Formát dosky / PD (napr. 2800×2070, 4100×600)
      x_mm: values.x_mm ? parseInt(values.x_mm, 10) || null : null,
      y_mm: values.y_mm ? parseInt(values.y_mm, 10) || null : null,
      dodavatel: finalDodavatel || null,
      vyrobca: vyrobca || null,
      // Štruktúra povrchu sa kusového (kovanie) netýka
      struktura_povrchu: druh === "kusovy" || !dekorovyTyp ? null : struktura || null,
      // "X" = drevná kresba (na dielci sa dá voliť smer), "0" = uni dekor.
      // Pri edite zachovaj existujúce "Y", meň len cez hranicu má/nemá.
      smer_vlakien_default: (druh === "kusovy"
        ? "0"
        : maStrukturu
          ? material?.smer_vlakien_default && material.smer_vlakien_default !== "0"
            ? material.smer_vlakien_default
            : "X"
          : "0") as "0" | "X" | "Y",
    };
    // Ručný smer (KOLO 168): nová položka ho dostane, keď user na smer
    // siahol; pri úprave ho server nastaví sám pri zmene smeru a výslovné
    // `false` ho vráti pravidlu importu

    try {
      if (isEdit && material) {
        await updateMutation.mutateAsync({
          id: material.id,
          data: riadiSaPravidlom ? { ...payload, smer_rucne: false } : payload,
        });
        toast({ title: "Materiál uložený" });
      } else {
        const { data: novy } = await createMutation.mutateAsync({
          ...payload,
          smer_rucne: smerRucne,
          je_aktivny: true,
          je_oblubene: false,
        });
        toast({ title: "Materiál pridaný", description: values.nazov });
        onCreated?.(novy);
      }
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  async function handleDelete(): Promise<void> {
    if (!material) return;
    if (
      !window.confirm(
        `Naozaj vymazať materiál "${material.nazov}"? ` +
          "Bude vyradený z cenníka (existujúce zákazky ho stále uvidia).",
      )
    ) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(material.id);
      toast({ title: "Materiál vyradený z cenníka" });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri mazaní",
        description: extractErrorMessage(err),
      });
    }
  }

  const dodavatelia = dodavateliaQuery.data?.data ?? [];
  const vyrobcovia = vyrobcoviaQuery.data?.data ?? [];
  /**
   * Čitateľné meno vybraného dodávateľa — v DB je kód (`twd`), user
   * pozná „TWD (Blum)" (KOLO 164). Neznámy kód ostane, ako je.
   */
  const nazovDodavatela = (kod: string): string =>
    dodavatelia.find((d) => d.kod === kod)?.nazov ??
    (material?.dodavatel === kod ? material?.dodavatel_nazov : null) ??
    kod;
  const idLabel =
    ID_LABEL[
      kodSkladany && !maUlozenyKod
        ? stlpecSkladanehoKodu(dodavatelText, hranipexHrana)
        : idColumnForDodavatel(dodavatelText, material)
    ];

  // Štruktúry filtrované podľa zvoleného výrobcu; bez výrobcu celý katalóg
  const filteredStruktury = vyrobca
    ? struktury.filter(
        (s) => s.vyrobca.toLowerCase() === vyrobca.toLowerCase(),
      )
    : [];
  const strukturyNaVyber =
    filteredStruktury.length > 0 ? filteredStruktury : struktury;

  const katPreVyber =
    kategoria === NEW_KATEGORIA ? novaKategoria.trim() : kategoria;
  /**
   * Má položka hrúbku dosky? Plošné vždy, dĺžkové len pracovná doska
   * a zástena — hrana má šírku a hrúbku pásky, nie hrúbku dosky.
   */
  const jeDoska =
    druh === "plosny" ||
    trieda === "pd" ||
    (druh === "dlzkovy" &&
      (katPreVyber.startsWith("PRACOVNA_DOSKA") ||
        katPreVyber.startsWith("ZASTENA")));
  /**
   * Hrana — šírka a hrúbka pásky z ponuky, bez formátu a smeru vlákien
   * (KOLO 169). Pracovná doska aj kovanie na metre sú dĺžkové, hranou nie.
   */
  const jeHrana = druh === "dlzkovy" && !jeDoska && trieda !== "kovanie";
  /** Kovanie na metre — kráti sa z tyče, nárezový plán potrebuje jej dĺžku. */
  const jeKovanieNaMetre = trieda === "kovanie" && druh === "dlzkovy";
  // Sekciu vie server (`Material.sekcia`) — platí, kým druh a kategória
  // ostali, ako ich uložil; pri zmene rozhodne odhad podľa kategórie
  const sekciaUlozena =
    material && druh === material.druh && katPreVyber === (material.kategoria ?? "")
      ? material.sekcia
      : undefined;
  const maOdpad = materialMaOdpad(druh, katPreVyber, sekciaUlozena);

  /**
   * Vlastné marže (`marze_rucne`) — import ani Nastavenia cien ich
   * neprepíšu, preto to okno musí povedať. „Riadiť sa kategóriou" doplní
   * čísla kategórie; po uložení server príznak zhodí sám (porovná ich
   * s kategóriou), takže položka sa zase hýbe s Nastaveniami cien.
   */
  const vlastneMarze = isEdit && !!material?.marze_rucne && !riadiSaKategoriou;
  /** Smer vlákien iný, než má položka uložený — uloženie ho urobí vlastným. */
  const smerZmeneny =
    isEdit && maStrukturu !== ((material?.smer_vlakien_default ?? "0") !== "0");
  const marzeAktualnejQuery = useMarzeKategorie(
    druh,
    vlastneMarze && !kategoriaZmenena && katPreVyber ? katPreVyber : null,
    smerPreMarze,
  );
  const marzeAktualnej = marzeAktualnejQuery.data?.data ?? null;

  function riadSaKategoriou(): void {
    if (!marzeAktualnej) return;
    setValue("rezia_pct", Number(marzeAktualnej.rezia_pct));
    setValue("praca_eur_per_mj", Number(marzeAktualnej.praca_eur_per_mj));
    if (maOdpad) setValue("nadmiera_pct", Number(marzeAktualnej.nadmiera_pct));
    setRiadiSaKategoriou(true);
  }
  /**
   * Nová položka bez kategórie sa uložiť nedá.
   *
   * Doska, pracovná doska aj hrana ju dostanú odvodenú z rozmerov
   * (`navrh-materialu`), takže ručne ju vyberá len kovanie. Edit
   * necháva staré položky na pokoji — kategóriu dopĺňa ich vlastná
   * oprava, nie zablokovaný formulár.
   */
  const chybaKategoria = !isEdit && !katPreVyber;
  /**
   * Kód sa skladá, ale ešte nevznikol — chýba pole, z ktorého sa skladá
   * (KOLO 169). Bez kódu by položku import nespároval a Cut Rite nepoznal.
   * Blokuje len novú položku: stará bez kódu sa musí dať opraviť aj
   * inak (cena, kategória) — ako pri kategórii, povie to, ale nezastaví.
   */
  const bezKodu = skladaSa && !(watch("id_kod") ?? "").trim();
  const chybaKod = !isEdit && bezKodu;
  /** Ponuka rozmerov hrany — uložená položka ukáže aj starý rozmer mimo nej. */
  const sirkyNaVyber =
    !skladaSa && sirkaHrany && !sirkyHran.includes(sirkaHrany)
      ? [...sirkyHran, sirkaHrany]
      : sirkyHran;
  const hrubkyNaVyber: readonly string[] =
    !skladaSa && hrubkaHrany && !(HRUBKY_HRAN as readonly string[]).includes(hrubkaHrany)
      ? [...HRUBKY_HRAN, hrubkaHrany]
      : HRUBKY_HRAN;


  /**
   * Výber štruktúry z katalógu. Pri hrane je to štruktúra **dosky**, ku
   * ktorej hrana patrí (KOLO 228 — user: „aby si to v edite hrany lepšie
   * pomenoval a umiestnil, že tá štruktúra nie je hrany, ale dosky, ku
   * ktorej sa páruje") — preto stojí pri dekore dosky, nie pri kategórii.
   */
  const poleStruktury = (
    <div className="space-y-1.5">
      <Label htmlFor="m-struktura">
        {jeHrana ? "Štruktúra dosky" : "Štruktúra povrchu"}
        {hranipexHrana && skladaSa ? " *" : ""}
      </Label>
      {/* Výber z katalógu, nie voľný text — `pd`, `PD.` ani
          `Pd` sa už napísať nedajú a kód materiálu tým
          prestal závisieť od toho, ako to kto napísal. */}
      <Select
        value={struktura || BEZ_STRUKTURY}
        onValueChange={(v) => {
          if (!v) return; // tá istá ozvena ako pri kategórii
          setStruktura(v === BEZ_STRUKTURY ? "" : v);
        }}
      >
        <SelectTrigger id="m-struktura">
          <SelectValue placeholder="Vyber štruktúru" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={BEZ_STRUKTURY}>— bez štruktúry</SelectItem>
          {/* Hodnotu z odkazu ukáž aj keď v katalógu ešte nie je */}
          {struktura &&
          !strukturyNaVyber.some((x) => x.kod === struktura) ? (
            <SelectItem value={struktura}>{struktura}</SelectItem>
          ) : null}
          {strukturyNaVyber.map((x) => (
            <SelectItem key={x.id ?? `${x.vyrobca}|${x.kod}`} value={x.kod}>
              {x.kod} — {x.nazov}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/* User: „ten kto pridáva dekor nemôže ho pridať v inej
          štruktúre." Tvrdý zákaz to nie je — W960 má reálne
          SM aj PE — ale musí byť vidieť, čo dekor už má, aby
          druhá štruktúra nevznikla omylom. */}
      {navrhKodu && navrhKodu.struktury_dekoru.length > 0 ? (
        <p className="text-[11px] text-muted-foreground">
          {sledovanyDekor} už v cenníku má:{" "}
          {navrhKodu.struktury_dekoru
            .map(
              (x) =>
                `${x.kod}${
                  x.hrubky.length
                    ? ` (${x.hrubky
                        .map((h) => Number(h))
                        .join(", ")} mm)`
                    : ""
                }`,
            )
            .join(", ")}
        </p>
      ) : null}
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? `Upraviť materiál — ${material?.nazov}`
              : kopia
                ? `Nový materiál — kópia z ${kopia.nazov}`
                : "Nový materiál"}
          </DialogTitle>
          <DialogDescription>
            Pri zmene cien sa neaktualizujú existujúce zákazky retroaktívne.
          </DialogDescription>
        </DialogHeader>
        {/* Výpredaj a „nie je v cenníku" aj s vysvetlením — v tabuľke je
            len v tooltipe (KOLO 166) */}
        {isEdit && material ? <StavZnaky material={material} vysvetlenie /> : null}
        {/* Sprievodca — len pre nový materiál. Edit ide rovno na
            formulár; tam už človek vie, čo mení. */}
        {!isEdit && krok === "co" ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Čo pridávaš? Podľa toho sa appka spýta len na to, čo daný
              druh naozaj má.
            </p>
            {CO_PRIDAVAM.map((v) => (
              <button
                key={v.kluc}
                type="button"
                className="w-full rounded-md border px-3 py-2 text-left hover:bg-accent"
                onClick={() => {
                  setTrieda(v.kluc);
                  setDruh(v.druh);
                  setMj(v.mj);
                  // Kód inej voľby (skladaný doske, opísaný kovaniu) nepatrí
                  setValue("id_kod", "");
                  // Späť a iná voľba nesmie niesť kategóriu predošlej —
                  // kovanie by sa uložilo ako pracovná doska
                  setKategoria(v.kategoria ?? "");
                  setKategoriaRucne(false);
                  setNovaKategoria("");
                  setValue("druh", v.druh);
                  setKrok("odkial");
                }}
              >
                <div className="text-sm font-medium">{v.nazov}</div>
                <div className="text-xs text-muted-foreground">{v.popis}</div>
              </button>
            ))}
          </div>
        ) : null}

        {!isEdit && krok === "odkial" ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Odkiaľ položka je? Z odkazu na eshop sa predvyplní dekor,
              štruktúra aj výrobca — nič sa nesťahuje, číta sa samotná
              adresa.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="m-eshop-krok">Odkaz z eshopu</Label>
              <Input
                id="m-eshop-krok"
                placeholder="https://www.jafholz.sk/shop/…/lamino-dtdl-kronospan-k682-pd-alpaca~p17384082"
                onChange={(e) => prevezmiOdkaz(e.target.value)}
              />
            </div>
            {dodavatel ? (
              <p className="text-xs">
                Dodávateľ:{" "}
                <span className="font-medium">
                  {dodavatel === NEW_DODAVATEL
                    ? novyDodavatel
                    : nazovDodavatela(dodavatel)}
                </span>
                {navrhKodu ? (
                  <span className="ml-2 text-muted-foreground">
                    {navrhKodu.napoveda}
                  </span>
                ) : null}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Dodávateľa vyberieš v ďalšom kroku — z odkazu sa
                predvyplní sám, keď ho appka pozná.
              </p>
            )}
            <div className="flex justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => setKrok("co")}
              >
                Späť
              </Button>
              <Button type="button" onClick={() => setKrok("udaje")}>
                Ďalej
              </Button>
            </div>
          </div>
        ) : null}

        {/* Formulár sa v prvých dvoch krokoch **nerenderuje** — skryť
            ho triedou by znamenalo dva rovnaké popisky v DOM-e. RHF si
            hodnoty nastavené cez `setValue` drží aj pre nezobrazené
            polia (`shouldUnregister` je default `false`). */}
        {isEdit || krok === "udaje" ? (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <fieldset disabled={!!lenCitanie} className="contents">
          {/* Komponenty setu — read-only (set vznikol cez Spojiť kovania) */}
          {setKomponenty.length > 0 ? (
            <div className="rounded-md border bg-background-alt/40 p-3 text-xs space-y-1">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                Komponenty setu ({setKomponenty.length})
              </div>
              {setKomponenty.map((c, i) => (
                <div key={i} className="flex justify-between gap-2">
                  <span className="truncate">
                    {(c.pocet ?? 1) > 1 ? `${c.pocet}× ` : ""}
                    {c.nazov}
                  </span>
                  <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                    {c.kod ?? ""}
                  </span>
                </div>
              ))}
              <p className="text-[11px] text-muted-foreground">
                Nákupná cena setu sa pri importe cenníkov prepočíta zo
                súčtu komponentov × kusy. Práca sa účtuje raz za set.
              </p>
            </div>
          ) : null}

          {/* Druh dosky (KOLO 167) — z neho plynie, z čoho sa skladá kód
              a do ktorej kategórie doska patrí. Edit ostáva bez neho. */}
          {!isEdit && druh === "plosny" ? (
            <div className="space-y-1.5">
              <Label htmlFor="m-typ-dosky">Druh dosky</Label>
              <Select
                value={typDosky}
                onValueChange={(v) => {
                  if (!v) return; // ozvena Radixu, ako pri kategórii
                  zmenTypDosky(v);
                }}
              >
                <SelectTrigger id="m-typ-dosky">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Predvolený typ aj kým sa register nenačítal */}
                  {typyDosiek.some((t) => t.kluc === typDosky) ? null : (
                    <SelectItem value={typDosky}>Laminovaná DTD</SelectItem>
                  )}
                  {typyDosiek.map((t) => (
                    <SelectItem key={t.kluc} value={t.kluc}>
                      {t.nazov}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {vybranyTyp ? (
                <p className="text-[11px] text-muted-foreground">
                  Kód napr. <span className="font-mono">{vybranyTyp.priklad}</span>
                </p>
              ) : null}
            </div>
          ) : null}

          {/* ID + Dekor (pre plošné/hrany hneď hore) */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="m-id">{idLabel}</Label>
              <Input
                id="m-id"
                placeholder={kodSkladany ? "zloží appka" : "napr. 56-3550 / F005036"}
                readOnly={kodSkladany}
                className={kodSkladany ? "font-mono bg-background-alt/60" : undefined}
                {...register("id_kod")}
              />
              {/* Kód sa **neháda** — appka ho skladá alebo povie, že sa
                  má opísať. To bola pôvodná otázka: „nevie, v akom
                  štýle mám písať ID." Kód dosky a hrany je od KOLA 169
                  **len skladaný**, bez ručného prepisu — inak by sa
                  schéma dala obísť a import by položku nespároval. */}
              {skladaSa && navrhKodu ? (
                <p className="text-xs text-muted-foreground">
                  Kód skladá appka: {navrhKodu.napoveda}.
                  {navrhKodu.existuje_nazov ? (
                    <span className="ml-1 text-destructive">
                      — už v cenníku je ({navrhKodu.existuje_nazov})
                    </span>
                  ) : null}
                </p>
              ) : kodSkladany && maUlozenyKod ? (
                <p className="text-[11px] text-muted-foreground">
                  Kód sa pri úprave nemení — páruje sa podľa neho import
                  cenníka a tak sa položka volá v Cut Rite.
                </p>
              ) : !isEdit && navrhKodu ? (
                <p className="text-xs text-muted-foreground">
                  {navrhKodu.napoveda}
                  {navrhKodu.existuje_nazov ? (
                    <span className="ml-1 text-destructive">
                      — už v cenníku je ({navrhKodu.existuje_nazov})
                    </span>
                  ) : null}
                </p>
              ) : null}
            </div>
            {druh !== "kusovy" && dekorovyTyp && !jeHrana ? (
              <div className="space-y-1.5">
                <Label htmlFor="m-dekor">Dekor</Label>
                <Input id="m-dekor" placeholder="8681 / U999 / W1000…" {...register("dekor_kod")} />
              </div>
            ) : vybranyTyp?.pole_zaklad && !jeHrana ? (
              <div className="space-y-1.5">
                <Label htmlFor="m-zaklad">
                  {vybranyTyp.pole_zaklad}
                  {vybranyTyp.zaklad_povinny ? " *" : ""}
                </Label>
                <Input
                  id="m-zaklad"
                  value={zaklad}
                  onChange={(e) => setZaklad(e.target.value)}
                  list="zaklad-list"
                />
                <datalist id="zaklad-list">
                  {(vybranyTyp.navrhy_zakladu ?? []).map((v) => (
                    <option key={v} value={v} />
                  ))}
                </datalist>
              </div>
            ) : (
              <div />
            )}
          </div>

          {/* Hrana: dekor a štruktúra patria doske, ku ktorej sa hrana páruje
              (KOLO 228) — jeden rámik, aby nebolo vidieť ako vlastnosť hrany */}
          {jeHrana ? (
            <fieldset
              className="space-y-2 rounded-md border px-3 pb-3 pt-1"
              data-testid="hrana-doska"
            >
              <legend className="px-1 text-xs font-semibold text-muted-foreground">
                Doska, ku ktorej hrana patrí
              </legend>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="m-dekor">Dekor dosky</Label>
                  <Input id="m-dekor" placeholder="8681 / U999 / W1000…" {...register("dekor_kod")} />
                </div>
                {poleStruktury}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Hrana sa ponúkne k doske tohto dekoru; pri rovnakom dekore má
                prednosť doska s touto štruktúrou.
                {hranipexHrana
                  ? " Pri Hranipexe povinné — podľa nej ďalší import hranu spáruje."
                  : " Nepovinné."}
              </p>
            </fieldset>
          ) : null}

          {/* Hrana Hranipexu sa volá propagačnou skupinou ako v importe
              (KOLO 169) — predpona z ponuky, číslo z eshopu či cenníka,
              lepidlo = „lep.". Pätica (skupina, dekor, šírka, hrúbka,
              štruktúra) je identita, podľa ktorej ju ďalší import nájde. */}
          {hranipexHrana && skladaSa ? (
            <div className="space-y-1.5">
              <div className="grid grid-cols-[6rem_1fr_auto] items-end gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="m-predpona">Predpona *</Label>
                  <Select
                    value={predpona}
                    onValueChange={(v) => {
                      if (!v) return; // ozvena Radixu, ako pri kategórii
                      setPredpona(v);
                    }}
                  >
                    <SelectTrigger id="m-predpona">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {PREDPONY_HRANIPEX.map((pr) => (
                        <SelectItem key={pr} value={pr}>
                          {pr}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="m-cislo-skupiny">Číslo skupiny *</Label>
                  <Input
                    id="m-cislo-skupiny"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="211381"
                    value={cisloSkupiny}
                    onChange={(e) =>
                      setCisloSkupiny(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                  />
                </div>
                <label className="flex h-10 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={lepidlo}
                    onChange={(e) => setLepidlo(e.target.checked)}
                    className="h-3.5 w-3.5 accent-primary cursor-pointer"
                  />
                  Lepidlo (lep.)
                </label>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Propagačná skupina z eshopu alebo cenníka Hranipexu (napr.
                HD 211381). Dekor, šírka, hrúbka a štruktúra sú povinné —
                podľa nich ďalší import cenníka hranu nájde.
              </p>
            </div>
          ) : null}

          {/* Odkaz z eshopu — z adresy sa odvodí dodávateľ aj kód.
              Nič sa nesťahuje: parsuje sa reťazec. Čítanie HTML eshopov
              by znamenalo štyri parsery, ktoré sa rozbijú pri prvom
              redizajne obchodu. */}
          <div className="space-y-1.5">
            <Label htmlFor="m-eshop">Odkaz z eshopu</Label>
            <Input
              id="m-eshop"
              placeholder="https://www.hranipex.sk/sk/hladat/?q=HD29160"
              {...register("eshop_url")}
              onPaste={(e) => {
                const vlozene = e.clipboardData.getData("text");
                // Nová položka: z adresy idú polia, z ktorých sa kód skladá
                if (!isEdit) {
                  prevezmiOdkaz(vlozene);
                  return;
                }
                const { dodavatel: dod, kod } = rozoberOdkaz(vlozene);
                if (dod) setDodavatel(dod);
                // Uložený kód dosky a hrany sa nemení (KOLO 169)
                if (kod && !kodSkladany) setValue("id_kod", kod, { shouldDirty: true });
              }}
            />
            {errors.eshop_url ? (
              <p className="text-xs text-destructive">
                {errors.eshop_url.message}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                {kodSkladany
                  ? "Po vložení sa predvyplní dodávateľ a polia, z ktorých appka skladá kód."
                  : "Po vložení sa predvyplní dodávateľ aj kód."}{" "}
                Tlačidlo Eshop potom ide priamo na produkt, nie na vyhľadávanie.
              </p>
            )}
          </div>

          {/* Názov */}
          <div className="space-y-1.5">
            <Label htmlFor="m-nazov">Názov *</Label>
            <Input
              id="m-nazov"
              autoFocus
              {...register("nazov", { onChange: () => setNazovRucne(true) })}
            />
            {errors.nazov ? (
              <p className="text-xs text-destructive">{errors.nazov.message}</p>
            ) : null}
          </div>

          {/* Druh + MJ */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="m-druh">Druh *</Label>
              {/* Kovanie na metre je vnútri dĺžkové (KOLO 176), ale stále
                  kovanie — o kusoch a metroch rozhoduje MJ. Pole ukazovalo
                  „Hrany" a výber „Kovanie" vrátil MJ na ks (KOLO 192). */}
              <Select
                value={trieda === "kovanie" ? "kusovy" : druh}
                onValueChange={(v) => handleDruhChange(v as DruhMaterialu)}
              >
                <SelectTrigger id="m-druh"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(DRUH_LABELS) as DruhMaterialu[]).map((d) => (
                    <SelectItem key={d} value={d}>{DRUH_LABELS[d]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="m-mj">MJ *</Label>
              <Select value={mj} onValueChange={(v) => zmenMj(v as MJ)}>
                <SelectTrigger id="m-mj"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ALLOWED_MJ.map((u) => (
                    <SelectItem key={u} value={u}>{MJ_LABEL[u] ?? u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Hrúbka dosky — **z nej plynie kategória a marže**. Do
              KOLA 92 pole vôbec neexistovalo, takže ručne pridaná
              doska mala hrúbku prázdnu, kód sa nezložil a import
              filtrovaný hrúbkou ju nikdy nenašiel. */}
          {jeDoska ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="m-hrubka-dosky">Hrúbka dosky (mm) *</Label>
                <Input
                  id="m-hrubka-dosky"
                  inputMode="decimal"
                  placeholder="napr. 18 alebo 0,8"
                  {...register("z_mm", {
                    // Čiarka sa po opustení poľa prepíše na bodku (user:
                    // „dovoľ aj čiarku 0,8 mm, potom to prehoď na 0.8")
                    onBlur: (e: React.FocusEvent<HTMLInputElement>) =>
                      setValue("z_mm", hrubkaZTextu(e.target.value)),
                  })}
                />
                <p className="text-[11px] text-muted-foreground">
                  Podľa nej sa určí kategória, marže aj formát.
                </p>
              </div>
              <div />
            </div>
          ) : null}

          {/* Šírka + Hrúbka — len pre hrany, **z ponuky** (KOLO 169): šírky,
              ktoré cenník má, a hrúbky pások z cenníka Hranipexu. Voľný
              text robil kódy, ktoré nikto nedohľadal. */}
          {jeHrana ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="m-sirka">Šírka hrany (mm){skladaSa ? " *" : ""}</Label>
                <Select
                  value={sirkaPovolena ? sirkaHrany : ""}
                  onValueChange={(v) => {
                    if (!v) return; // ozvena Radixu, ako pri kategórii
                    setValue("sirka_mm", v);
                  }}
                >
                  <SelectTrigger id="m-sirka">
                    <SelectValue placeholder="Vyber šírku" />
                  </SelectTrigger>
                  <SelectContent>
                    {sirkyNaVyber.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s} mm
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="m-hrubka">Hrúbka hrany (mm){skladaSa ? " *" : ""}</Label>
                <Select
                  value={hrubkaPovolena ? hrubkaHrany : ""}
                  onValueChange={(v) => {
                    if (!v) return; // ozvena Radixu, ako pri kategórii
                    setValue("hrubka_mm", v);
                  }}
                >
                  <SelectTrigger id="m-hrubka">
                    <SelectValue placeholder="Vyber hrúbku" />
                  </SelectTrigger>
                  <SelectContent>
                    {hrubkyNaVyber.map((h) => (
                      <SelectItem key={h} value={h}>
                        {h.replace(".", ",")} mm
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : null}

          {/* Formát — dosky aj pracovnej dosky (šírka je v jej kóde);
              hrana ani kovanie formát nemajú */}
          {jeDoska ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="m-format-x">Formát — dĺžka (mm)</Label>
                <Input
                  id="m-format-x"
                  placeholder="napr. 2800"
                  {...register("x_mm", { onChange: () => setFormatRucne(true) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="m-format-y">Formát — šírka (mm)</Label>
                <Input
                  id="m-format-y"
                  placeholder="napr. 2070"
                  {...register("y_mm", { onChange: () => setFormatRucne(true) })}
                />
              </div>
              {formatVKode ? (
                <p className="col-span-2 text-[11px] text-muted-foreground">
                  Formát je súčasť kódu — každý formát je vlastná položka
                  (smer vlákien ide po dĺžke).
                </p>
              ) : null}
            </div>
          ) : null}

          {/* Dĺžka tyče — z čoho sa kovanie na metre kráti (KOLO 176).
              Re-import cenníka ju doplní len do prázdneho, ručná vyhrá. */}
          {jeKovanieNaMetre ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="m-dlzka-tyce">Dĺžka tyče (mm)</Label>
                <Input
                  id="m-dlzka-tyce"
                  inputMode="numeric"
                  placeholder="napr. 3000"
                  {...register("x_mm")}
                />
              </div>
              <p className="self-end pb-2 text-[11px] text-muted-foreground">
                Z tejto tyče počíta nárezový plán a objednávka. Rolku
                (kefa, LED pás) zadaj celou dĺžkou.
              </p>
            </div>
          ) : null}

          {/* Dodávateľ + Výrobca/Kolekcia */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="m-dodavatel">Dodávateľ</Label>
              <Select
                value={dodavatel}
                onValueChange={(v) => {
                  // Radix vo formulári ohlási prázdnu hodnotu, keď efekt
                  // nastaví dodávateľa skôr, než prídu položky zoznamu
                  // (ako pri kategórii). Prázdny sa vybrať nedá — bez tejto
                  // poistky by úprava uložila materiál bez dodávateľa.
                  if (!v) return;
                  setDodavatel(v);
                }}
              >
                <SelectTrigger id="m-dodavatel">
                  <SelectValue placeholder="Vyber dodávateľa" />
                </SelectTrigger>
                <SelectContent>
                  {/* Aktuálny dodávateľ aj keď v zozname nie je (nový
                      kód z odkazu, skrytý dodávateľ) — inak prázdny výber */}
                  {dodavatel &&
                  dodavatel !== NEW_DODAVATEL &&
                  !dodavatelia.some((d) => d.kod === dodavatel) ? (
                    <SelectItem value={dodavatel}>
                      {nazovDodavatela(dodavatel)}
                    </SelectItem>
                  ) : null}
                  {dodavatelia.map((d) => (
                    <SelectItem key={d.kod} value={d.kod}>{d.nazov}</SelectItem>
                  ))}
                  <SelectItem value={NEW_DODAVATEL}>+ Nový dodávateľ…</SelectItem>
                </SelectContent>
              </Select>
              {dodavatel === NEW_DODAVATEL ? (
                <Input
                  placeholder="Názov nového dodávateľa"
                  value={novyDodavatel}
                  onChange={(e) => setNovyDodavatel(e.target.value)}
                  className="mt-1"
                />
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="m-vyrobca">
                {jeHrana ? "Kolekcia" : "Výrobca"}
              </Label>
              <Input
                id="m-vyrobca"
                placeholder="Kronospan / Egger / Blum…"
                value={vyrobca}
                onChange={(e) => setVyrobca(e.target.value)}
                list="vyrobcovia-list"
              />
              <datalist id="vyrobcovia-list">
                {vyrobcovia.map((v) => <option key={v} value={v} />)}
              </datalist>
            </div>
          </div>

          {/* Kategória + Štruktúra */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="m-kategoria">Kategória</Label>
              <Select
                value={kategoria || ""}
                onValueChange={(v) => {
                  // Radix ohlási prázdnu hodnotu aj vtedy, keď value
                  // zmenil odvodzovací efekt (položka nie je v DOM-e,
                  // kým je zoznam zatvorený). Prázdna kategória sa
                  // v zozname vybrať nedá, takže je to vždy ozvena —
                  // bez tejto poistky by odvodenie samo seba vyplo.
                  if (!v) return;
                  setKategoria(v);
                  if (v !== navrhKodu?.kategoria) setKategoriaRucne(true);
                }}
              >
                <SelectTrigger id="m-kategoria">
                  <SelectValue placeholder="Vyber kategóriu" />
                </SelectTrigger>
                <SelectContent>
                  {/* Aktuálnu kategóriu zobraz aj keď nie je v options */}
                  {kategoria && kategoria !== NEW_KATEGORIA && !kategorieOptions.includes(kategoria) ? (
                    <SelectItem value={kategoria}>{formatKategoria(kategoria)}</SelectItem>
                  ) : null}
                  {kategorieOptions.map((k) => (
                    <SelectItem key={k} value={k}>{formatKategoria(k)}</SelectItem>
                  ))}
                  <SelectItem value={NEW_KATEGORIA}>+ Nová kategória…</SelectItem>
                </SelectContent>
              </Select>
              {kategoria === NEW_KATEGORIA ? (
                <Input
                  placeholder="Názov novej kategórie"
                  value={novaKategoria}
                  onChange={(e) => setNovaKategoria(e.target.value)}
                  className="mt-1"
                />
              ) : null}
              {marzeNovej ? (
                <p className="text-[11px] text-muted-foreground">
                  {formatKategoria(marzeNovej.kategoria)} má prácu{" "}
                  {marzeNovej.praca_eur_per_mj} €/{mj || "mj"} a réžiu{" "}
                  {formatPct(marzeNovej.rezia_pct)}.{" "}
                  <button
                    type="button"
                    onClick={pouziMarzeKategorie}
                    className="font-medium text-primary underline underline-offset-2"
                  >
                    Použiť
                  </button>
                </p>
              ) : null}
              {isEdit ? (
                <p className="text-[11px] text-muted-foreground">
                  Zmenu zaradenia si appka zapamätá — re-import cenníka ju
                  už neprepíše.
                </p>
              ) : null}
            </div>
            {druh !== "kusovy" && !dekorovyTyp ? (
              vybranyTyp?.pole_povrch ? (
                <div className="space-y-1.5">
                  <Label htmlFor="m-povrch">{vybranyTyp.pole_povrch}</Label>
                  <Input
                    id="m-povrch"
                    value={povrch}
                    onChange={(e) => setPovrch(e.target.value)}
                    list="povrch-list"
                  />
                  <datalist id="povrch-list">
                    {(vybranyTyp.navrhy_povrchu ?? []).map((v) => (
                      <option key={v} value={v} />
                    ))}
                  </datalist>
                </div>
              ) : (
                <div />
              )
            ) : trieda !== "kovanie" && !jeHrana ? (
              poleStruktury
            ) : (
              <div />
            )}
          </div>

          {/* Smer vlákien má len doska — hrana ani kovanie (KOLO 169) */}
          {jeDoska ? (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={maStrukturu}
                onChange={(e) => {
                  setMaStrukturu(e.target.checked);
                  setSmerRucne(true);
                  setRiadiSaPravidlom(false);
                }}
                className="h-3.5 w-3.5 accent-primary cursor-pointer"
              />
              Má štruktúru (smer vlákien)
              <span className="text-[11px] text-muted-foreground">
                — drevené dekory áno, uni farby nie
              </span>
            </label>
          ) : null}
          {/* Ručný smer (KOLO 168) — import cenníka ho už neprepíše; po
              zmene ho server nastaví sám, preto to platí už pred uložením */}
          {jeDoska && isEdit && riadiSaPravidlom ? (
            <p className="text-[11px] text-muted-foreground">
              Smer sa po uložení riadi pravidlom — ďalší import ho nastaví sám.
            </p>
          ) : jeDoska && isEdit && (material?.smer_rucne || smerZmeneny) ? (
            <p className="text-[11px] text-muted-foreground">
              Vlastný smer — import ho neprepíše.{" "}
              {material?.smer_rucne && !smerZmeneny ? (
                <button
                  type="button"
                  onClick={() => setRiadiSaPravidlom(true)}
                  className="font-medium text-primary underline underline-offset-2"
                >
                  Riadiť sa pravidlom
                </button>
              ) : null}
            </p>
          ) : null}

          <Separator />

          {/* Cena */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="m-cena">Nákupná cena (€/MJ) *</Label>
              <Input id="m-cena" inputMode="decimal" {...register("nakup_cena")} />
              {errors.nakup_cena ? (
                <p className="text-xs text-destructive">{errors.nakup_cena.message}</p>
              ) : null}
            </div>
            <div />
          </div>

          {/* Marže sú **odvodené z kategórie** — tie isté čísla, aké
              dostane materiál pri importe cenníka (`pricing_rules.yaml`).
              Pýtať sa na ne pri každej položke znamená len možnosť
              rozísť sa s importom; kto ich potrebuje inak, rozbalí ich. */}
          <div className="rounded-md border bg-background-alt/40 p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                {vlastneMarze ? (
                  <>
                    <span className="font-medium text-foreground">
                      Vlastné marže
                    </span>
                    : práca {watch("praca_eur_per_mj")} €/{mj}, réžia{" "}
                    {watch("rezia_pct")} %
                    {maOdpad ? <>, nadmiera {watch("nadmiera_pct")} %</> : null}
                    . Import ani Nastavenia cien ich neprepíšu.{" "}
                    {marzeAktualnej ? (
                      <button
                        type="button"
                        onClick={riadSaKategoriou}
                        className="font-medium text-primary underline underline-offset-2"
                      >
                        Riadiť sa kategóriou
                      </button>
                    ) : null}
                  </>
                ) : katPreVyber ? (
                  <>
                    Podľa kategórie{" "}
                    <span className="font-medium text-foreground">
                      {formatKategoria(katPreVyber)}
                    </span>
                    : práca {watch("praca_eur_per_mj")} €/{mj}, réžia{" "}
                    {watch("rezia_pct")} %
                    {maOdpad ? <>, nadmiera {watch("nadmiera_pct")} %</> : null}
                  </>
                ) : (
                  <>
                    Kategória zatiaľ nie je známa — {jeDoska
                      ? "doplň hrúbku dosky"
                      : "vyber kategóriu"}
                    , marže sa dopočítajú.
                  </>
                )}
              </p>
              <button
                type="button"
                className="shrink-0 text-xs underline underline-offset-2"
                onClick={() => setMarzeRucne((v) => !v)}
              >
                {marzeRucne ? "Skryť" : "Upraviť"}
              </button>
            </div>
            {marzeRucne ? (
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="m-rezia">Réžia (%)</Label>
                  <Input id="m-rezia" inputMode="decimal" {...register("rezia_pct")} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="m-praca">Práca (€/MJ)</Label>
                  <Input id="m-praca" inputMode="decimal" {...register("praca_eur_per_mj")} />
                </div>
                {maOdpad ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="m-nadmiera">Nadmiera (%)</Label>
                    <Input id="m-nadmiera" inputMode="decimal" {...register("nadmiera_pct")} />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          </fieldset>
          {lenCitanie ? (
            <DialogFooter className="!justify-between">
              <span className="text-xs text-muted-foreground">{lenCitanie}</span>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Zavrieť
              </Button>
            </DialogFooter>
          ) : (
          <DialogFooter className="!justify-between">
            <div className="flex items-center gap-1">
              {isEdit ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleDelete}
                  className="text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                  Vymazať z cenníka
                </Button>
              ) : null}
              {isEdit && material && !material.je_set ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setKopia(material)}
                  title="Nová položka s parametrami tejto — kód a odkaz sa vyplnia znova"
                >
                  <CopyPlus className="h-4 w-4" />
                  Vytvoriť kópiu
                </Button>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              {/* Prečo sa nedá uložiť, musí byť **napísané** — samotné
                  vypnuté tlačidlo nikto neuhádne (lekcia z KOLA 58).
                  Bez kategórie nemá položka odkiaľ vziať prácu a réžiu
                  a v cenníku sa hľadá ťažko. */}
              {chybaKategoria ? (
                <span className="text-xs text-destructive">
                  Vyber kategóriu — z nej plynie práca aj réžia
                </span>
              ) : chybaKod ? (
                <span className="text-xs text-destructive">
                  Doplň údaje, z ktorých appka skladá kód
                </span>
              ) : bezKodu ? (
                <span className="text-xs text-muted-foreground">
                  Položka nemá kód — doplň údaje a appka ho zloží
                </span>
              ) : null}
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                Zrušiť
              </Button>
              <Button type="submit" disabled={isSubmitting || chybaKategoria || chybaKod}>
                {isSubmitting ? "Ukladá sa…" : isEdit ? "Uložiť" : "Pridať"}
              </Button>
            </div>
          </DialogFooter>
          )}
        </form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Drevina a kvalita zo skladaného kódu dosky (KOLO 167):
 * `SKAROVKA-BUK_19mm_AB_4100x630` → `{ zaklad: "BUK", povrch: "AB" }`.
 * Dekorové kódy (`U113_18mm_ST9`) typ s pomlčkou nemajú → `null`.
 */
export function rozlozKodDosky(
  kod: string | null | undefined,
): { zaklad: string; povrch: string | null } | null {
  const m = /^[A-Z]+-([^_]+)_[\d.,]+mm(?:_([^_]+))?(?:_\d+x\d+)?$/i.exec(kod ?? "");
  if (!m) return null;
  const povrch = m[2] && !/^\d+x\d+$/i.test(m[2]) ? m[2] : null;
  return { zaklad: m[1]!, povrch };
}

function defaultsFromMaterial(
  m: Material | null,
  pred?: Predvyplnenie,
): FormData {
  return {
    id_kod: primaryIdOf(m),
    nazov: m?.nazov ?? pred?.nazov ?? "",
    druh: (m?.druh ?? pred?.druh ?? "plosny") as DruhMaterialu,
    mj: ((m?.mj as MJ | undefined) ?? (pred?.mj as MJ | undefined) ?? "m2") as MJ,
    dekor_kod: m?.dekor_kod ?? pred?.dekor_kod ?? "",
    eshop_url: m?.eshop_url ?? "",
    nakup_cena: m ? Number(m.nakup_cena) : 0,
    rezia_pct: m ? Number(m.rezia_pct) : 20,
    praca_eur_per_mj: m ? Number(m.praca_eur_per_mj) : 0,
    nadmiera_pct: m ? Number(m.nadmiera_pct) : 0,
    z_mm: m?.z_mm ?? pred?.z_mm ?? "",
    sirka_mm: m?.sirka_mm ?? pred?.sirka_mm ?? "",
    x_mm: m?.x_mm != null ? String(m.x_mm) : "",
    y_mm: m?.y_mm != null ? String(m.y_mm) : "",
    hrubka_mm: m?.hrubka_mm ?? pred?.hrubka_mm ?? "",
  };
}
